import $ from "jquery";
import ko from "knockout";
import deferred from "flow/promise/deferred";
import when from "flow/promise/when";
import MyFlow from "flow/MyFlow";
import protect from "flow/protect";
import config from "flow/config";
import cssLoader from "flow/utils/cssLoader";
import dataService from "flow/data/service";
import manifestManager from "flow/utils/manifestManager";
import environment from "flow/configuration/environment";
import validationHelper from "flow/utils/validationHelper";
import FeedbackHelper from "flow/validation/FeedbackHelper";
import { ICommand, IButtonGroupCommand, ICommandAdapter } from "./interfaces/ICommand";
import { Dictionary } from "./api/types";
import { makeObservableIfNeeded } from "./koHelpers/makeObservable";
import { IDimension } from "./utils/dialog";
import { defaults as knockoutValidationDefaults } from "flow/knockout/validationSetup";

function commandEnabled(command: ICommand): boolean {
    return command.isEnabled === undefined || ko.unwrap(command.isEnabled) === true;
}

function mapButtonGroupCommands(buttonGroupParent: ICommandAdapter): IButtonGroupCommand[] {
    return $.map(buttonGroupParent.buttonGroup, function (command) {
        return {
            click: function () {
                buttonGroupParent.activeButtonGroupCmd(command);
            },
            text: makeObservableIfNeeded(command.text, ""),
            isVisible: makeObservableIfNeeded(command.isVisible, true),
            isEnabled:
                command.isEnabled !== undefined
                    ? makeObservableIfNeeded(command.isEnabled)
                    : makeObservableIfNeeded(buttonGroupParent.isEnabled),
            commandId: makeObservableIfNeeded(command.commandId),
            cmdReference: command
        };
    });
}

let instance_counter = 0;

class ViewportOrder {
    base = this.manifestOrder ?? 0;
    overriddenBase = ko.observable<number>(undefined);
    computedBase = ko.pureComputed<number>(() => this.overriddenBase() ?? this.base);

    activationNumber = ko.observable<number>(0);
    old = ko.observable<boolean>(false);
    tiebreaker = ko.pureComputed<number>(() => this.activationNumber() + (this.old() ? 20000 : 0));

    constructor(private readonly manifestOrder: number) {}

    compare(other: ViewportOrder): number {
        const result = this.computedBase() - other.computedBase();
        return result === 0 ? this.tiebreaker() - other.tiebreaker() : result;
    }
}

export default class Component {
    cuid = "flow_component_" + ++instance_counter;
    isBroken = ko.observable<boolean>(false);
    isLoading = ko.observable<boolean>(false);
    viewHtml = ko.observable<string>();
    detailHtml = ko.observable<string>();
    notificationsHtml = ko.observable<string>();
    viewModel = ko.observable<any>();
    x = 0;
    y = 0;
    order = new ViewportOrder(this.manifest.order);
    minimizedWidth = this.manifest.minimizedWidth;
    minimizedHeight = this.manifest.minimizedHeight;
    isMaximized = ko.observable<boolean>(false);
    adjustedWidth = ko.observable<boolean>(false);
    adjustedHeight = ko.observable<boolean>(false);
    isVisible = ko.observable<boolean>(true);
    isSelected = ko.observable<boolean>(false);

    isShadow = this.manifest.isShadow ? ko.observable<boolean>(this.manifest.isShadow) : ko.observable<boolean>(false);
    element = ko.observable<JQuery<HTMLElement>>();
    contentElement: HTMLElement = null;
    inspectionEnabled = ko.observable<boolean>(environment.isEnabled(environment.keys.inspector));
    notificationText = ko.observable<string>(undefined);
    notificationType = ko.observable<number>();
    notificationOpaque = ko.observable<boolean>(false);
    notificationSticky = ko.observable<boolean>(false);
    originalTitle = ko.observable<string>(this.manifest.name);
    publishOnTitleChange = false;
    footerText = ko.pureComputed<string>(() => {
        if (this.viewModel() && this.viewModel().footerText) {
            if (
                this.viewModel().footerText() !== undefined &&
                this.viewModel().footerText() !== null &&
                this.viewModel().footerText().length > 0
            ) {
                return this.viewModel().footerText();
            }
        }
        return "";
    });

    inEdit = ko.pureComputed<boolean>(() => {
        if (
            this.viewModel() &&
            this.viewModel().inEdit &&
            this.viewModel().inEdit() !== undefined &&
            this.viewModel().inEdit() !== null
        ) {
            return this.viewModel().inEdit();
        }

        return false;
    });

    isDirty = ko.pureComputed<boolean>(() => {
        // Preparation for Flow 3
        if (this.viewModel() && this.viewModel().isDirty) {
            return ko.utils.unwrapObservable(this.viewModel().isDirty);
        }

        return false;
    });

    counter = ko.pureComputed<boolean>(() => {
        if (this.viewModel() && this.viewModel().counter) {
            return ko.utils.unwrapObservable(this.viewModel().counter);
        }

        return undefined;
    });

    hasCounter = ko.pureComputed(() => this.counter() !== undefined);

    expanderWantsVisibility = ko.pureComputed<boolean>(() => {
        if (this.viewModel() && this.viewModel().expanderWantsVisibility) {
            const result = protect(this.myFlow, this.viewModel(), this.viewModel().expanderWantsVisibility)();
            return result !== undefined ? result : false;
        }
        return true;
    });

    canRemember = ko.pureComputed<boolean>(() => {
        return this.viewModel() && this.viewModel().canRemember && ko.unwrap(this.viewModel().canRemember);
    });

    alreadyLoading = false;

    myFlow = new MyFlow(this.manifest, {
        fatal: () => {
            this.isBroken(true);
        },
        exception: () => {
            this.isBroken(true);
            if (this.isVisible()) {
                this.myFlow.notifyError(
                    this.myFlow.translate(this.manifest.name) +
                        ": " +
                        this.myFlow.translate("FlowHtml.Translations.UnknownError")
                );
            }
        },
        startLoading: () => {
            this.isLoading(true);
        },
        stopLoading: () => {
            this.isLoading(false);
        },
        onFocus: () => {
            this.onFocus();
        },
        isSelected: this.isSelected,
        authorizationManager: manifestManager.getWebModuleAuthorizationManager(this.manifest.moduleId),
        resizeComponent: () => {
            this.resizeComponent();
        },
        component: this,
        rootComponent: this.injectedMyFlow ? this.injectedMyFlow.settings.rootComponent : this
    });

    title = ko.pureComputed<string>({
        read: () => {
            let retValue = this.originalTitle();
            if (this.viewModel() && this.viewModel().componentTitle) {
                if (
                    this.viewModel().componentTitle() !== undefined &&
                    this.viewModel().componentTitle() !== null &&
                    this.viewModel().componentTitle().length > 0
                ) {
                    retValue = this.viewModel().componentTitle();
                    this.publishOnTitleChange = true;
                }
            }
            if (this.publishOnTitleChange && manifestManager.isComponent(this.manifest.id)) {
                this.myFlow.publish(config.topics.containerTitleChanged);
            }
            return retValue;
        },
        write: newTitle => {
            this.originalTitle(newTitle);
            this.publishOnTitleChange = true;
        }
    });

    bottomNavigationTitle = ko.pureComputed<string>(() => {
        if (this.manifest.useDynamicBottomNavigationTitle === true) {
            return this.title();
        }
        return this.originalTitle();
    });

    commands = ko.pureComputed<ICommandAdapter[]>(() => {
        if (this.viewModel() !== undefined && this.viewModel() !== null) {
            let cmds = [];
            if (this.viewModel().commands) {
                cmds = [].concat(this.viewModel().commands());
            }

            if (this.manifest.canMaximize) {
                this.addResizeComponentCommand(cmds);
            }

            return this.mapCommands(cmds);
        }
        return [];
    });

    loadDfd = deferred<void>(this.myFlow);
    safeLoadDfd = deferred<void>(this.myFlow);

    containerSearchVisible = ko.pureComputed<boolean>(() => {
        return this.viewModel() && this.viewModel().containerSearchVisible
            ? ko.utils.unwrapObservable(this.viewModel().containerSearchVisible)
            : false;
    });

    containerSearchPlaceholder = ko.pureComputed<string>(() => {
        return this.viewModel() && this.viewModel().containerSearchPlaceholder
            ? ko.utils.unwrapObservable(this.viewModel().containerSearchPlaceholder)
            : "";
    });

    containerSearchPhrase = ko.observable<string>("");

    constructor(
        public readonly manifest: any,
        public readonly removeInstance: (instance: Component) => void,
        private readonly injectedMyFlow?: MyFlow
    ) {
        // maybe this could be fixed in other way?
        this.manifest.minimizedWidth = this.manifest.minimizedWidth
            ? parseInt(this.manifest.minimizedWidth)
            : this.manifest.minimizedWidth;
        this.manifest.minimizedHeight = this.manifest.minimizedHeight
            ? parseInt(this.manifest.minimizedHeight)
            : this.manifest.minimizedHeight;

        this.isSelected.subscribe((newValue: boolean) => {
            if (newValue === false && !this.notificationSticky()) this.myFlow.clearLocalNotification();
        });

        this.notificationText.subscribe(() => {
            if (this.onContainerSizeChanged) {
                this.onContainerSizeChanged();
            }
        });
    }

    clickCommand(command: ICommand, viewModel: any, event: MouseEvent): void {
        if (command.requireConfirmation === true) {
            this.myFlow
                .showConfirmDialog(
                    command.confirmationTitle
                        ? command.confirmationTitle
                        : this.myFlow.translate("FlowHtml.Translations.Cancel"),
                    command.confirmationText
                        ? command.confirmationText
                        : this.myFlow.translate("FlowHtml.Translations.CancelConfirmation"),
                    this.myFlow.translate("FlowHtml.Translations.Yes"),
                    this.myFlow.translate("FlowHtml.Translations.No")
                )
                .done(function (result: boolean) {
                    if (result === true) {
                        command.click(viewModel, event);
                    }
                });
        } else {
            command.click(viewModel, event);
        }
    }

    mapCommands(commands: ICommand[]): ICommandAdapter[] {
        const mappedCommands: ICommandAdapter[] = $.map(commands, command => {
            return {
                click: (viewModel: any, event: MouseEvent) => {
                    this.myFlow.clearLocalNotification();

                    if (commandEnabled(command)) {
                        if (command.validate === true) {
                            if (this.useValidationV3()) {
                                const validatableObject = command.validatableObject
                                    ? ko.unwrap(command.validatableObject)
                                    : undefined;
                                const errors = this.getValidationErrorsV3(validatableObject);

                                if (errors().length === 0) {
                                    this.clickCommand(command, viewModel, event);
                                } else {
                                    errors.showAllMessages();
                                    this.focusFirstErrorV3();
                                    this.logValidationErrorsV3(validatableObject);
                                }
                            } else {
                                this.clickCommand(command, viewModel, event);
                            }
                        } else {
                            this.clickCommand(command, viewModel, event);
                        }
                    }
                },
                icon: makeObservableIfNeeded(command.icon),
                text: makeObservableIfNeeded(command.text),
                isVisible: makeObservableIfNeeded(command.isVisible, true),
                isEnabled: makeObservableIfNeeded(command.isEnabled, true),
                commandId: makeObservableIfNeeded(command.commandId),
                commandText: makeObservableIfNeeded(command.commandText),
                isEdit: command.isEdit ? command.isEdit : false,
                buttonGroup: command.buttonGroup ? command.buttonGroup : undefined,
                buttonClass: makeObservableIfNeeded(command.buttonClass, "btn-default"),
                focusObs: makeObservableIfNeeded(command.focusObs)
            };
        });

        for (let i = 0; i < mappedCommands.length; i++) {
            if (mappedCommands[i].buttonGroup) {
                mappedCommands[i].mappedButtonGroup = ko.observableArray(mapButtonGroupCommands(mappedCommands[i]));
                if (mappedCommands[i].activeButtonGroupCmd == null) {
                    mappedCommands[i].activeButtonGroupCmd = ko.observable(mappedCommands[i].buttonGroup[0]);
                }

                mappedCommands[i].activeButtonGroupCmd.subscribe(function (cmd) {
                    if (cmd != null && cmd.click != null) {
                        cmd.click(cmd, undefined); // Review
                    }
                });
            }
        }

        return mappedCommands;
    }

    addResizeComponentCommand(cmds: ICommand[]): void {
        cmds.push({
            icon: ko.pureComputed<string>(() => {
                return this.isMaximized() ? "lc-icon-Arrows-diagonal-inward" : "lc-icon-Arrows-diagonal-1";
            }),
            text: ko.pureComputed<string>(() => {
                return this.isMaximized()
                    ? this.myFlow.translate("FlowHtml.Translations.Minimize")
                    : this.myFlow.translate("FlowHtml.Translations.Maximize");
            }),
            click: () => {
                this.resizeComponent();
            },
            isVisible: ko.pureComputed<boolean>(() => {
                return !this.adjustedHeight() || !this.adjustedWidth();
            })
        });
    }

    resizeComponent(): void {
        this.myFlow.publish(config.topics.resizeComponent, this);

        if (this.isMaximized() && this.viewModel() && this.viewModel().onMaximized) {
            this.viewModel().onMaximized();
        } else if (this.viewModel() && this.viewModel().onMinimized) {
            this.viewModel().onMinimized();
        }
    }

    setElement(element: JQuery<HTMLElement>): void {
        this.element(element);
    }

    hasScroll(): boolean {
        return $(this.contentElement).get(0).scrollHeight > $(this.contentElement).height();
    }

    containerSearchCallback(searchPhrase: string): void {
        if (this.viewModel() && this.viewModel().onContainerSearch) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onContainerSearch)(searchPhrase);
        }
    }

    containerSearchClick(): void {
        this.containerSearchCallback(this.containerSearchPhrase());
    }

    containerSearchKeyDown(_d: any, e: KeyboardEvent): boolean {
        if (e.keyCode === $.ui.keyCode.ESCAPE && this.containerSearchPhrase() != "") {
            this.containerSearchPhrase("");
            this.containerSearchClick();
            e.stopPropagation();
        }

        return true;
    }

    containerSearchKeyPress(_d: any, e: KeyboardEvent): boolean {
        if (e.keyCode === $.ui.keyCode.ENTER) {
            this.containerSearchClick();
        }

        return true;
    }

    load(): JQuery.Promise<void> {
        if (this.alreadyLoading) {
            return this.loadDfd.promise();
        }

        this.alreadyLoading = true;

        if (this.manifest.useDefaultViewModel === true) {
            this.manifest.viewModelId = config.defaultViewModelIds.viewModel;
            this.manifest.namespace = "default";
        }

        if (this.manifest.bundle) {
            return dataService
                .loadBundle(this.manifest.bundle)
                .then(this.internalLoad.bind(this))
                .catch(this.componentLoadingFailure.bind(this));
        } else {
            return this.internalLoad();
        }
    }

    internalLoad(): JQueryPromise<void> {
        cssLoader.loadBatch(this.manifest.cssUrls);
        const vmQuery = dataService.getViewModel(this.myFlow, this.manifest.viewModelId);
        const htmlQuery = dataService.getView(this.myFlow, this.manifest.viewUrl);
        let detailQuery;
        let notificationQuery;

        if (
            this.manifest.isWorkingContext === true &&
            this.manifest.detailViewUrl !== undefined &&
            this.manifest.detailViewUrl !== null
        ) {
            detailQuery = dataService.getView(this.myFlow, this.manifest.detailViewUrl);
        } else {
            detailQuery = [""];
        }

        if (
            this.manifest.isWorkingContext === true &&
            this.manifest.notificationsViewUrl !== undefined &&
            this.manifest.notificationsViewUrl !== null
        ) {
            notificationQuery = dataService.getView(this.myFlow, this.manifest.notificationsViewUrl);
        } else {
            notificationQuery = [""];
        }

        when(this.myFlow, vmQuery, htmlQuery, detailQuery, notificationQuery)
            .done((vmArgs, htmlArgs, detailArgs, notificationArgs) => {
                this.setupComponent(vmArgs, htmlArgs, detailArgs, notificationArgs);
            })
            .fail(this.componentLoadingFailure.bind(this));

        return this.loadDfd.promise();
    }

    componentLoadingFailure(...params: any[]): void {
        this.myFlow.fatal(params[0] as Error, ...params.slice(1));
        this.loadDfd.reject(...params);
    }

    setupComponent(ViewModel: any, view: string, detailView: string, notificationView: string): void {
        protect(this.myFlow, this, function () {
            if (!this.isShadow()) {
                this.viewHtml(view);
                this.detailHtml(detailView);
                this.notificationsHtml(notificationView);

                if (this.manifest.namespace) {
                    ViewModel = ViewModel[this.manifest.namespace];
                } else if (this.manifest.isEmbedded) {
                    if (ViewModel && ViewModel.default) {
                        ViewModel = ViewModel.default;
                    }
                }

                const vm = new ViewModel(this.myFlow);
                vm.__myFlow = this.myFlow;
                this.viewModel(vm);

                // Bug fix: the component seems to resize itself when its inner html changes.
                // In Viewport we do initial rendering before element is set!
                if (
                    !this.manifest.isMenu &&
                    !this.manifest.isDialog &&
                    !this.manifest.isWorkingContext &&
                    !this.manifest.isEmbedded
                ) {
                    this.myFlow.publish("component.setupComponent");
                }
            }
            this.loadDfd.resolve();
        })();

        if (this.isBroken()) {
            this.loadDfd.reject();
        }
    }

    safeLoad(): JQueryPromise<void> {
        this.load().catch(() => {
            this.safeLoadDfd.reject();
        });
        return this.safeLoadDfd.promise();
    }

    activate(viewContext: Dictionary, extraParam1?: any, extraParam2?: any, extraParam3?: any): JQueryPromise<any> {
        // Extra params for dialog
        if (this.viewModel() && this.viewModel().activate) {
            const promise = protect(this.myFlow, this.viewModel(), this.viewModel().activate)(
                viewContext,
                extraParam1,
                extraParam2,
                extraParam3
            );
            this.myFlow.publish(config.topics.componentActivated);
            if (promise !== undefined) {
                return promise;
            }
        }

        const dfd = deferred(this.myFlow);

        if (this.isBroken()) {
            dfd.reject();
        } else {
            dfd.resolve();
        }

        return dfd.promise();
    }

    onShow(): void {
        if (this.viewModel() && this.viewModel().onShow) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onShow)();
        }
    }

    onHide(): void {
        if (this.viewModel() && this.viewModel().onHide) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onHide)();
        }
    }

    onDestroy(): void {
        if (this.viewModel() && this.viewModel().onDestroy) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onDestroy)();
        }
        this.removeInstance(this);
        this.myFlow.unsubscribeAll();
    }

    canLeave(id?: string): string {
        if (this.viewModel() && this.viewModel().canLeave) {
            const result = protect(this.myFlow, this.viewModel(), this.viewModel().canLeave)(id);

            if (result === false) {
                return this.myFlow.translate("FlowHtml.Translations.YouHaveUnsavedData");
            } else if (result === true) {
                return undefined;
            }

            return result;
        }
        return undefined;
    }

    hasForm(element: HTMLElement): boolean {
        if (element !== undefined && element !== null) {
            return $(element).find("form").length > 0;
        } else {
            return false;
        }
    }

    onViewAttached(contentElement: HTMLElement): void {
        this.contentElement = contentElement;

        if (this.viewModel() && this.viewModel().onViewAttached) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onViewAttached)(contentElement);
        }

        this.safeLoadDfd.resolve();
    }

    onDetailViewAttached(element: HTMLElement): void {
        if (this.viewModel() && this.viewModel().onDetailViewAttached) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onDetailViewAttached)(element);
        }
    }

    onNotificationsViewAttached(element: HTMLElement): void {
        if (this.viewModel() && this.viewModel().onNotificationsViewAttached) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onNotificationsViewAttached)(element);
        }
    }

    shouldBeVisible(componentId: string, hintFromComponent: any): boolean {
        if (this.viewModel() && this.viewModel().shouldBeVisible) {
            const result = protect(
                this.myFlow,
                this.viewModel(),
                this.viewModel().shouldBeVisible
            )(componentId, hintFromComponent);
            return result !== undefined ? result : true;
        }
        return true;
    }

    hint(): any {
        if (this.viewModel() && this.viewModel().hint) {
            return protect(this.myFlow, this.viewModel(), this.viewModel().hint)();
        }
        return undefined;
    }

    onSelected(): void {
        if (this.viewModel() && this.viewModel().onSelected && this.viewModel().useOnSelectedCallback === true) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onSelected)();
        }
    }

    onFocus(): void {
        if (this.viewModel() && this.viewModel().onFocus) {
            protect(this.myFlow, this, this.viewModel().onFocus)($(this.contentElement));
        } else {
            const firstInput = $(this.contentElement).find("input, select").filter(":visible").first();
            if ($(firstInput).attr("type") != "image") {
                firstInput.focus();
            }
        }
    }

    onContainerSizeChanged(): void {
        if (this.viewModel() && this.viewModel().onContainerSizeChanged) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onContainerSizeChanged)();
        }
    }

    getDimensions(maxWidth?: number | string, maxHeight?: number | string): IDimension {
        if (this.viewModel() && this.viewModel().getDimensions) {
            return protect(this.myFlow, this.viewModel(), this.viewModel().getDimensions)(maxWidth, maxHeight);
        }

        return {
            width: "auto",
            height: "auto"
        };
    }

    toolbarButtonGroupAfterRender(htmlObjects: HTMLElement[]): void {
        if (htmlObjects == null || htmlObjects.length === 0) {
            return;
        }

        const btnGroup = htmlObjects.find(function (htmlObj) {
            return $(htmlObj).hasClass("btn-group");
        });

        if (btnGroup === null || btnGroup.children === null || btnGroup.children.length === 0) {
            return;
        }

        let maxButtonWidth = 0;
        const buttons = btnGroup.children;
        for (let i = 0; i < buttons.length; i++) {
            const buttonWidth = (buttons[i] as HTMLElement).offsetWidth;
            maxButtonWidth = Math.max(maxButtonWidth, buttonWidth);
        }

        for (let i = 0; i < buttons.length; i++) {
            $(buttons[i]).css("width", maxButtonWidth + 2);
        }
    }

    onBeforeNavigateBack(e: any): void {
        if (this.viewModel() && this.viewModel().onBeforeNavigateBack) {
            protect(this.myFlow, this.viewModel(), this.viewModel().onBeforeNavigateBack)(e);
        }
    }

    useValidationV3(): boolean {
        return this.manifest.useValidationV3 === true;
    }

    validationOptionsV3(): KnockoutValidationConfiguration {
        let customConfig = undefined;

        if (this.viewModel() && this.viewModel().validationOptionsV3) {
            customConfig = protect(this.myFlow, this.viewModel(), this.viewModel().validationOptionsV3)();
        }

        return $.extend({}, knockoutValidationDefaults, customConfig);
    }

    clearValidationErrorsV3(object?: object): void {
        if (object) {
            ko.validation.group(object, { deep: true }).showAllMessages(false);
        } else {
            ko.validation.group(this.viewModel(), { deep: true }).showAllMessages(false);
        }
    }

    getValidationErrorsV3(object?: object): KnockoutValidationErrors {
        if (object) {
            return ko.validation.group(object, { deep: true });
        } else {
            return ko.validation.group(this.viewModel(), { deep: true });
        }
    }

    focusFirstErrorV3(formElement?: HTMLFormElement): void {
        if (!formElement) {
            formElement = $(this.element()).find("form:visible").first().get(0) as HTMLFormElement;
        }

        if (formElement) {
            validationHelper.focusErrorElement(formElement);
        }
    }

    logValidationErrorsV3(object?: object): void {
        let list;
        if (object) {
            list = new FeedbackHelper().listInvalidFields(object);
        } else {
            list = new FeedbackHelper().listInvalidFields(this.viewModel());
        }

        if (list.length > 0) {
            for (let i = 0; i < list.length; i++) {
                this.myFlow.debug(list[i].field + ": '" + list[i].message + "'");
            }
        }
    }
}
